/**
 *
 *  TruBeDateTimePicker
 *  TruBe Date Time Picker Container Component
 *
 *  Created by Vladimir Shishlyannikov on 28/02/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React from "react";
import DayPicker from "./DayPicker/DayPicker";
import moment from "moment-timezone";

// Components
import TimeSlotsListing from "./TimeSlotsListing/TimeSlotsListing";

// Icons
import TimeIcon from "../../icons/TimeIcon";

// Utils
import getDates from "./helpers/dates";
import isSameDay from "./helpers/isSameDay";
import isEqual from "./helpers/isEqual";
import { addModifier, deleteModifier } from "./helpers/modifiers";
import getModifierKeyFull from "./helpers/getModifierKeyFull";
import { defaultDateFormat, getMomentWithTz, getTz } from "../../utils/date";
// import * as dates from "../../utils/dates";

import { availabilityCalendar as calendarConstants } from "../../constants";
// import { startOf } from "../../utils/dates";
// import { hours } from "../../utils/dates";
// import { minutes } from "../../utils/dates";
// import { seconds } from "../../utils/dates";
// import { milliseconds } from "../../utils/dates";

const defaultProps = {
  alternateFocusedInput: true,
  startDate: null,
  endDate: null,
  minDate: null,
  maxDate: null,
  focusedInput: calendarConstants.FocusedInput.START_DATE,
  onDatesChange() {},
  startDateOffset: null,
  endDateOffset: null,
  disabled: undefined,
  isOutsideRange: () => false,
  isDayBlocked: () => false,
  isDayHighlighted: () => false,
  openKeyboardShortcutsPanel() {},
  renderMonthText: undefined,
  enableOutsideDays: false,
  numberOfMonths: 1,
  orientation: calendarConstants.CalendarOrientation.HORIZONTAL,
  initialVisibleMonth: undefined,
  daySize: calendarConstants.DEFAULT_CALENDAR_DAY_SIZE,
  dayPickerNavigationInlineStyles: undefined,
  navPosition: calendarConstants.NavPosition.NAV_POSITION_TOP,
  navPrev: undefined,
  navNext: undefined,
  renderNavPrevButton: undefined,
  renderNavNextButton: undefined,
  noNavButtons: false,
  noNavNextButton: false,
  onPrevMonthClick() {},
  onNextMonthClick() {},
  onOutsideClick() {},
  onScrolled() {},
  renderCalendarDay: undefined,
  renderCalendarInfo: undefined,
  firstDayOfWeek: undefined,
  verticalHeight: undefined,
  transitionDuration: undefined,
  onBlur() {},
  isFocused: false,
  monthStartingHeadingLevel: 3,
  dayAriaLabelFormat: undefined,
};

export default class TruBeDateTimePicker extends React.PureComponent {
  constructor(props) {
    super(props);

    this.today = moment(new Date()).tz(getTz(props.region));

    this.modifiers = {
      today: (day) => this.isToday(day),
      blocked: (day) => this.isBlocked(day),
      "blocked-calendar": (day) => props.isDayBlocked(day),
      "blocked-out-of-range": (day) => props.isOutsideRange(day),
      "highlighted-calendar": (day) => props.isDayHighlighted(day),
      "selected-start": (day) => this.isStartDate(day),
      "selected-end": (day) => this.isEndDate(day),
      hovered: (t) => this.isHovered(t),
    };

    const { currentMonth, visibleDays } = this.getStateForNewMonth(props);

    this.state = {
      currentMonth,
      visibleDays: visibleDays,
      dateSelected: !!props.startDate,
      isTimeSelected: false,
    };

    this.onDayClick = this.onDayClick.bind(this);
    this.handleTimeSelect = this.handleTimeSelect.bind(this);
    this.handleTimeSlotSelect = this.handleTimeSlotSelect.bind(this);
    // this.onDayMouseEnter = this.onDayMouseEnter.bind(this);
    // this.onDayMouseLeave = this.onDayMouseLeave.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
        startDate: nextStartDate,
        isOutsideRange: nextIsOutsideRange,
        isDayBlocked: nextIsDayBlocked,
        isDayHighlighted: nextIsDayHighlighted,
        numberOfMonths: nextNumberOfMonths,
        enableOutsideDays: nextEnableOutsideDays,
      } = nextProps,
      {
        startDate,
        isOutsideRange,
        isDayBlocked,
        isDayHighlighted,
        numberOfMonths,
        enableOutsideDays,
      } = this.props;

    let { visibleDays } = this.state,
      hasIsOutsideRangeChanged = false,
      hasIsDayBlockedChanged = false,
      hasIsDayHighlightedChanged = false;

    if (nextIsOutsideRange !== isOutsideRange) {
      this.modifiers["blocked-out-of-range"] = (t) => nextIsOutsideRange(t);

      // eslint-disable-next-line
      hasIsOutsideRangeChanged = true;
    }

    if (nextIsDayBlocked !== isDayBlocked) {
      this.modifiers["blocked-calendar"] = (t) => nextIsDayBlocked(t);

      // eslint-disable-next-line
      hasIsDayBlockedChanged = true;
    }

    if (nextIsDayHighlighted !== isDayHighlighted) {
      this.modifiers["highlighted-calendar"] = (t) => nextIsDayHighlighted(t);

      // eslint-disable-next-line
      hasIsDayHighlightedChanged = true;
    }

    const hasStartDateChanged = !isSameDay(nextStartDate, startDate);

    if (
      nextNumberOfMonths !== numberOfMonths ||
      nextEnableOutsideDays !== enableOutsideDays
    ) {
      const newState = this.getStateForNewMonth(nextProps),
        { currentMonth, visibleDays } = newState;

      this.setState({
        currentMonth,
        visibleDays,
      });
    }

    let newVisibleDays = {};

    if (hasStartDateChanged) {
      newVisibleDays = this.deleteModifier(
        newVisibleDays,
        startDate,
        "selected-start"
      );

      if (newVisibleDays) {
        newVisibleDays = this.addModifier(
          newVisibleDays,
          nextStartDate,
          "selected-start"
        );

        // if (startDate) {
        //   const startDate_t = startDate.clone().add(1, "day");
        // }
      }

      if (nextStartDate) {
        this.setState({ dateSelected: true });
      }
    }

    const todayDate = moment(new Date()).tz(getTz(this.props.region));

    if (!isSameDay(this.today, todayDate)) {
      newVisibleDays = this.deleteModifier(newVisibleDays, this.today, "today");

      newVisibleDays = this.addModifier(newVisibleDays, todayDate, "today");

      this.today = todayDate;
    }

    Object.keys(newVisibleDays).length > 0 &&
      this.setState({
        visibleDays: {
          ...visibleDays,
          ...newVisibleDays,
        },
      });
  }

  onDayClick(day, event) {
    const { region, onBlur, focusedInput, onDatesChange } = this.props;
    var { isTimeSelected } = this.state;

    let { startDate, endDate } = this.props;

    var dateTime =
      this.state.isTimeSelected && startDate
        ? moment(
            merge(
              day.toDate(),
              startDate.toDate(),
              this.props.currentDate,
              region
            )
          ).tz(getTz(region))
        : day.startOf("day");

    var dateChanged = /*startDateRef.current !== null || */ !dateTime.isSame(
      startDate,
      "day"
    );

    if (!dateChanged) {
      isTimeSelected = false;

      this.setState({ dateSelected: false, isTimeSelected });
    }

    onDatesChange(
      {
        startDate: dateTime,
        endDate,
        focusedInput: focusedInput,
      },
      isTimeSelected ? true : undefined
    );

    // this.setState({ dateSelected: true });

    onBlur();
  }

  // visibleDays Object
  // { '2022-10': [{ moment }, ..],
  //   ..
  // }
  //
  // Returns days in the following format
  // {
  //   '2023-02': {
  //     '2023-02-01': Set { 'blocked' },
  //     '2023-02-02': Set { 'blocked' },
  //     ...
  //   },
  //   '2023-02': { ... }
  //   ...
  // }
  //
  getModifiers(visibleDays) {
    const modifiers = {};

    Object.keys(visibleDays).forEach((key) => {
      modifiers[key] = {};

      visibleDays[key].forEach((val) => {
        modifiers[key][getModifierKeyFull(val)] = this.getModifiersForDay(val);
      });
    });

    return modifiers;
  }

  getModifiersForDay(day) {
    return new Set(
      Object.keys(this.modifiers).filter((key) => {
        const modifierCb = this.modifiers[key];

        return modifierCb && modifierCb(day);
      })
    );
  }

  getStateForNewMonth(props) {
    const {
        enableOutsideDays,
        initialVisibleMonth,
        numberOfMonths,
        orientation,
        startDate,
      } = props,
      vertical =
        orientation ===
        calendarConstants.CalendarOrientation.VERTICAL_SCROLLABLE,
      currentMonth =
        initialVisibleMonth ||
        (startDate ? () => startDate : () => this.today)();

    return {
      currentMonth: currentMonth,
      visibleDays: this.getModifiers(
        getDates(currentMonth, numberOfMonths, enableOutsideDays, vertical)
      ),
    };
  }

  addModifier(modifiers, date, key) {
    return addModifier(modifiers, date, key, this.props, this.state);
  }

  deleteModifier(modifiers, date, key) {
    return deleteModifier(modifiers, date, key, this.props, this.state);
  }

  isEndDate(date) {
    const { endDate } = this.props;

    return isEqual(date, endDate);
  }

  isHovered(t) {
    return false;
  }

  isStartDate(date) {
    const { startDate } = this.props;

    return isEqual(date, startDate);
  }

  isBlocked(date, s = true) {
    const { isDayBlocked, isOutsideRange } = this.props;

    return isDayBlocked(date) || isOutsideRange(date);
  }

  isToday(date) {
    return isSameDay(date, this.today);
  }

  handleTimeSelect(datum) {
    const { disabled, format, region, onTimeChange } = this.props;

    var dateTime = this.props.startDate
        ? merge(
            this.props.startDate.toDate(),
            datum.date,
            this.props.currentDate,
            region
          )
        : datum.date,
      dateStr = moment(/*getMomentWithLATimezone*/ dateTime)
        .tz(getTz(region))
        .format(format || "YYYY-MM-DD", this.props.culture);

    if (!disabled) {
      onTimeChange(
        {
          dateTime,
          dateStr,
        },
        /*timeChanged: */ true
      );
    }
  }

  handleTimeSlotSelect(evt) {
    const target = evt.target;
    const timeSlotLabel = target.closest("button").textContent;
    const formattedLATime = `${getMomentWithTz(
      new Date(),
      this.props.region
    ).format("YYYY-MM-DD")} ${timeSlotLabel}`;
    const datum = moment.tz(
      formattedLATime,
      "YYYY-MM-DD h:mmA",
      getTz(this.props.region) //"America/Los_Angeles"
    );
    const { disabled, format, region, onTimeChange } = this.props;

    var dateTime = this.props.startDate
        ? merge(
            this.props.startDate.toDate(),
            datum.toDate(),
            this.props.currentDate,
            region
          )
        : datum.toDate(),
      dateStr = getMomentWithTz(dateTime, region).format(
        format || "YYYY-MM-DD",
        this.props.culture
      );

    var newDate = getMomentWithTz(dateTime, region);

    var timeChanged =
      this.props.startDate &&
      (this.props.startDate.hours() !== newDate.hours() ||
        this.props.startDate.minutes() !== newDate.minutes());

    if (!disabled) {
      onTimeChange(
        {
          dateTime,
          dateStr,
        },
        timeChanged
      );

      if (!this.state.isTimeSelected) {
        this.setState({ isTimeSelected: true });
      } else if (!timeChanged) {
        this.setState({ isTimeSelected: false });
      }
    }
  }

  renderTimeList() {
    var { activeOptionId, inputId, listId } = this;
    var {
      open,
      timeSlots,
      min,
      max,
      step,
      currentDate,
      startDate,
      date,
      culture,
      region,
      groupBy,
      preserveDate,
      timeFormat,
      timeComponent,
      renderGroup,
      disabledTimeSlots,
    } = this.props;

    return (
      <div
        style={{
          width: "100%",
        }}
      >
        <div
          className="daypicker-time-label tbk-absolute tbk-hidden lg:tbk-flex lg:tbk-items-center"
          style={{
            left: "-140px",
            bottom: "16px",
            alignItems: "center",
          }}
        >
          <span className="tbk-mr-1 tbk-text-green">
            <TimeIcon size={32} />
          </span>
          <div className="tbk-text-main tbk-text-blue-dark">Time:</div>
        </div>
        <TimeSlotsListing
          id={listId}
          timeSlots={timeSlots}
          disabled={disabledTimeSlots}
          min={min}
          max={max}
          step={step}
          currentDate={currentDate}
          activeId={activeOptionId}
          format={timeFormat}
          culture={culture}
          region={region}
          value={
            this.state.isTimeSelected || (startDate && !isStartOfDay(startDate)) // || currentDate
              ? dateOrNull(startDate && startDate.toDate())
              : null
          }
          groupBy={groupBy}
          onMove={this.handleScroll}
          onTimeSlotPress={this.handleTimeSlotSelect}
          preserveDate={preserveDate || !!date}
          itemComponent={timeComponent}
          renderGroup={renderGroup}
          aria-labelledby={inputId}
          aria-live={open && "polite"}
          aria-hidden={!open}
          messages={this.messages}
        />
      </div>
    );
  }

  render() {
    const {
        dayAriaLabelFormat,
        region,
        daySize,
        enableOutsideDays,
        min,
        // numberOfMonths,
        // onOutsideClick,
        renderCalendarDay,
      } = this.props,
      { currentMonth, visibleDays } = this.state;

    return (
      <div className="tb-date-time-picker tbk-flex-grow">
        <DayPicker
          currentMonth={currentMonth}
          min={min}
          dayAriaLabelFormat={dayAriaLabelFormat}
          region={region}
          daySize={daySize}
          enableOutsideDays={enableOutsideDays}
          modifiers={visibleDays}
          onDayClick={this.onDayClick}
          onScrolled={this.props.onScrolled}
          // onDayMouseEnter={this.onDayMouseEnter}
          // onDayMouseLeave={this.onDayMouseLeave}
          renderCalendarDay={renderCalendarDay}
        />
        <hr className="tbk-my-0 tbk-mx-2 tbk-border-blue-grey-light" />
        <div
          className="tbk-h-4"
          style={{
            overflow: this.state.dateSelected ? "" : "hidden",
          }}
        >
          <div
            className={`time-list-container tbk-relative tbk-mx-auto tbk-flex tbk-flex-row tbk-items-center tbk-justify-center md:tbk-max-w-[912px] md:tbk-pl-1.5 md:tbk-pr-1.5 md:tbk-pl-4 md:tbk-pr-4`}
            style={{
              transform: `translateY(${this.state.dateSelected ? 0 : "-100%"})`,
            }}
          >
            {this.renderTimeList()}
          </div>
        </div>
      </div>
    );
  }
}

TruBeDateTimePicker.defaultProps = defaultProps;

function dateOrNull(dt) {
  if (dt && !isNaN(dt.getTime())) {
    return dt;
  }
  return null;
}

const merge = (date, time, defaultDate, region) => {
  var dateCloned = getMomentWithTz(date, region);
  var timeCloned = getMomentWithTz(time, region);

  dateCloned = dateCloned.startOf("day");
  dateCloned.hours(timeCloned.hours());
  dateCloned.minutes(timeCloned.minutes());
  dateCloned.seconds(timeCloned.seconds());

  return dateCloned.toDate();
};

/**
 * date - moment date
 */
const isStartOfDay = (date) => {
  return date.hours() === 0 && date.minutes() === 0 && date.seconds() === 0;
};
